<template>
  <div class="bar" :class="{ 'bar--fixed': barFixed || !isHomePage }">
    <div class="zy_constr">
      <a class="bar-logo" href="/"></a>

      <div class="bar-nav">
        <div
          v-for="item in shownNav"
          :key="item.id"
          class="bar-nav__item"
          :class="{ 'bar-nav__item--active': activeAnchor === item.id }"
        >
          <a v-if="isHomePage" :key="item.id" class="bar-nav__link" @click.prevent="toAnchor(item.id)">{{
            item.name
          }}</a>
          <router-link v-else :key="item.id" class="bar-nav__link" :to="{ path: '/home', query: { id: item.id } }">{{
            item.name
          }}</router-link>
        </div>
      </div>

      <div class="bar-dropdown">
        <div class="bar-dropdown__title">加入掌阅</div>
        <div class="bar-dropdown__list">
          <div class="bar-dropdown__wrapper">
            <a target="_blank" href="https://q7w8vltyes.jobs.feishu.cn/zhangyue" class="bar-dropdown__item">社会招聘</a>
            <a target="_blank" href="https://q7w8vltyes.jobs.feishu.cn/2024" class="bar-dropdown__item">校园招聘</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { NAV } from '@/config';
import { scrollTo } from '@/utils/scroll-to';
import { throttle } from '@/utils/utils';

const HEADER_HEIGHT = 80;
const SCROLL_THROTTLE = 150;

export default {
  name: 'BarPC',
  data: function () {
    return {
      NAV, // 导航列表
      activeAnchor: '',
      scrollTop: 0,
      anchorPositions: [] // 存储所有锚点的位置信息
    };
  },
  computed: {
    isHomePage() {
      const path = this.$route.path;
      return path === '/' || path === '/home';
    },
    barFixed() {
      return this.scrollTop >= 1;
    },
    shownNav() {
      return this.NAV.filter(item => item.nav);
    }
  },
  watch: {
    '$route.path': {
      handler() {
        this.init();
      }
    }
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    this.removeScrollListener();
    this.removeResizeListener();
  },
  methods: {
    getZoomLevel() {
      const zoom = parseFloat(getComputedStyle(document.documentElement).zoom);
      if (!isNaN(zoom) && zoom > 0) {
        return zoom;
      }
    },

    init() {
      if (!this.isHomePage) return;
      this.$nextTick(() => {
        this.initScrollListener();
        this.updateAnchorPositions();
        this.initResizeListener();
        // 初始化时更新一次激活状态
        this.updateActiveAnchor();
        if (this.$route.query?.id) {
          this.toAnchor(this.$route.query.id);
        }
      });
    },

    // 初始化滚动监听
    initScrollListener() {
      this.handleScroll = throttle(this.onScroll, SCROLL_THROTTLE);
      window.addEventListener('scroll', this.handleScroll);
    },

    // 移除滚动监听
    removeScrollListener() {
      if (this.handleScroll) {
        window.removeEventListener('scroll', this.handleScroll);
      }
    },

    // 初始化窗口大小变化监听
    initResizeListener() {
      this.handleResize = throttle(() => {
        this.updateAnchorPositions();
        this.updateActiveAnchor();
      }, 300);
      window.addEventListener('resize', this.handleResize);
    },

    // 移除窗口大小变化监听
    removeResizeListener() {
      if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }
    },

    // 滚动事件处理
    onScroll() {
      const zoom = this.getZoomLevel();
      this.scrollTop = (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop) / zoom;
      this.updateActiveAnchor();
    },

    // 更新锚点位置信息
    updateAnchorPositions() {
      const zoom = this.getZoomLevel();
      this.anchorPositions = [];
      this.shownNav.forEach(anchor => {
        const element = document.getElementById(anchor.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
          const top = (rect.top + scrollTop) / zoom;
          this.anchorPositions.push({
            id: anchor.id,
            top: top,
            bottom: top + rect.height / zoom
          });
        }
      });

      // 按位置排序
      this.anchorPositions.sort((a, b) => a.top - b.top);
    },

    // 更新当前激活的锚点
    updateActiveAnchor() {
      const scrollTop = this.scrollTop;
      const zoom = this.getZoomLevel();
      const viewportHeight = window.innerHeight / zoom;
      const threshold = HEADER_HEIGHT / zoom;

      let newActiveAnchor = null;

      // 如果没有锚点，直接返回
      if (this.anchorPositions.length === 0) {
        return;
      }

      // 获取第一个锚点的位置
      const firstAnchor = this.anchorPositions[0];

      // 如果滚动位置在第一个锚点之前，不高亮任何导航项
      if (scrollTop + threshold < firstAnchor.top) {
        newActiveAnchor = null;
      } else {
        // 从下往上查找当前在视口中的锚点
        for (let i = this.anchorPositions.length - 1; i >= 0; i--) {
          const anchor = this.anchorPositions[i];

          // 如果锚点顶部在视口内，且考虑了头部高度
          if (anchor.top <= scrollTop + threshold) {
            newActiveAnchor = anchor.id;
            break;
          }
        }

        // 如果没找到，且页面滚动到底部，激活最后一个锚点
        if (!newActiveAnchor && scrollTop + viewportHeight >= document.body.scrollHeight / zoom - 100) {
          const lastAnchor = this.anchorPositions[this.anchorPositions.length - 1];
          if (lastAnchor) {
            newActiveAnchor = lastAnchor.id;
          }
        }
      }

      // 更新激活状态
      if (newActiveAnchor !== this.activeAnchor) {
        this.activeAnchor = newActiveAnchor;
      }
    },

    // 点击导航项跳转到对应锚点
    toAnchor(id) {
      const element = document.getElementById(id);
      if (element) {
        const zoom = this.getZoomLevel();
        const rect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
        const targetTop = (rect.top + scrollTop) / zoom - HEADER_HEIGHT / zoom;
        scrollTo(targetTop * zoom, 500);
      }
    }
  }
};
</script>
<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/function.scss';

.bar {
  height: 80px;
  width: 100%;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;

  &--fixed {
    background-color: #fff;
  }
}

.bar-logo {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  width: 126px;
  height: 36px;
  background-size: cover;
  background-image: url('https://static-efe-front-h.zhangyuecdn.com/sfm-production/zycom/da3328e8-dbc9-4f94-92db-789f14b059bd.png');
}

.bar--fixed {
  .bar-logo {
    background-image: url('https://static-efe-front-h.zhangyuecdn.com/sfm-production/zycom/10d83caf-794c-4aae-b2da-0b16001da584.png');
  }
}

.bar-nav {
  position: absolute;
  top: 50%;
  left: 206px;
  display: flex;
  transform: translateY(-50%);
}

.bar-nav__item {
  min-width: 70px;
  margin-left: 32px;
  &:first-child {
    margin-left: 0;
  }

  &--active {
    .bar-nav__link {
      color: $mainActive !important;
      box-shadow:
        0 1px $mainActive,
        0 2px $mainActive,
        0 3px $mainActive;
    }
  }
  &:hover {
    .bar-nav__link {
      color: $mainActive !important;
      box-shadow: none;
    }
  }
}

.bar-nav__link {
  color: #fff;
  padding: 8px 0;
  cursor: pointer;
  display: inline-block;
  height: 18px;
  line-height: 18px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.bar--fixed {
  .bar-nav__link {
    color: rgba(0, 0, 0, 0.85);
  }
}

.bar-dropdown {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);

  &:hover {
    .bar-dropdown__list {
      display: block;
    }
  }
}

.bar-dropdown__title {
  font-size: 14px;
  transition: all 0.3s ease;
  height: 30px;
  line-height: 30px;
  padding: 0 16px;
  border-radius: 16px;
  border: 1px solid $mainText;
  color: #fff;
  cursor: pointer;
}

.bar--fixed {
  .bar-dropdown__title {
    color: $mainActive;
    border: 1px solid $mainActive;
  }
}

.bar-dropdown__list {
  display: none;
  z-index: 200;
  position: absolute;
  top: 32px;
  box-sizing: border-box;
  right: 0;
  padding-top: 24px;
  width: 120px;
}

.bar-dropdown__wrapper {
  background: #fff;
  text-align: center;
}

.bar-dropdown__item {
  display: block;
  height: 48px;
  line-height: 48px;
  color: rgba(0, 0, 0, 0.85);
  &:hover {
    color: $mainActive;
  }
}
</style>
